import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../shared/stores/auth";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  // useAuth.persist.hasHydrated() is the Zustand v5 built-in way to check
  // whether the persist middleware has finished reading from localStorage.
  // We read it synchronously so that if Zustand hydrates synchronously (common
  // with localStorage) the spinner never even flashes.
  const [hydrated, setHydrated] = useState(() => useAuth.persist.hasHydrated());
  const auth = useAuth();
  const loc = useLocation();

  useEffect(() => {
    if (hydrated) return;

    // Subscribe to hydration-complete event.
    const unsub = useAuth.persist.onFinishHydration(() => setHydrated(true));

    // Safety valve: if the event never fires (stale SW, corrupted storage,
    // Zustand edge-case), force through after 3 s so the app never hangs.
    const id = setTimeout(() => setHydrated(true), 3000);

    return () => {
      unsub();
      clearTimeout(id);
    };
  }, [hydrated]);

  // Wait for Zustand persist to finish reading from localStorage.
  // Without this, API calls fire before the token is loaded → 401.
  if (!hydrated) {
    return (
      <div style={{
        height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--bg, #F6F7F9)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 36, height: 36, border: "3px solid rgba(99,102,241,0.2)",
            borderTopColor: "var(--p-500, #6366f1)", borderRadius: "50%",
            animation: "spin 0.75s linear infinite",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ fontSize: 13, color: "var(--text-3, #6B7280)" }}>로딩 중…</span>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated()) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ role, children }: { role: string; children: ReactNode }) {
  const hasRole = useAuth((s) => s.hasRole(role) || s.hasRole("SUPER_ADMIN"));
  if (!hasRole) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: "var(--text-3)" }}>
        <h2>접근 권한이 없습니다</h2>
        <p>이 페이지는 <strong>{role}</strong> 역할이 필요합니다.</p>
      </div>
    );
  }
  return <>{children}</>;
}

/** Allow access if ANY of the given menu keys is accessible in the manifest. */
export function RequireMenu({ anyOf, children }: { anyOf: string[]; children: ReactNode }) {
  const manifest = useAuth((s) => s.manifest);
  const ok = anyOf.some((k) => manifest?.menus?.[k] === true);
  if (!ok) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: "var(--text-3)" }}>
        <h2>접근 권한이 없습니다</h2>
        <p>이 페이지에 접근할 권한이 없습니다.</p>
      </div>
    );
  }
  return <>{children}</>;
}
